/**
 * sampleInfoHelpers.js
 *
 * Pure helpers for the shared sample-info logic used by step 3 and step 4.
 * The goal is to keep step 4 preview aligned with the actual sample form.
 */

import { getRNAseqPipelineInfo } from './rnaSeqPipelineHelpers';
import { getScRNAseqPipelineInfo } from './ScRnaSeqPipelineHelpers';
import {
  countChIPseqSubmittedSamples,
  getChIPseqPipelineInfo,
  isChIPseqServiceConfig
} from './chipSeqHelpers';
import { getScRnaExperimentSampleSheetRule } from './scRnaExperimentHelpers';

export const getUniqueOrderCategories = (data = {}) => {
  const categories = new Set();

  if (Array.isArray(data?.selectedServiceCategories)) {
    data.selectedServiceCategories.forEach(category => {
      if (category) categories.add(category);
    });
  }

  if (Array.isArray(data?.serviceItems)) {
    data.serviceItems.forEach(item => {
      if (item?.category) categories.add(item.category);
    });
  }

  return [...categories];
};

export const isAnalysisOnlyOrder = (data = {}) => {
  const categories = getUniqueOrderCategories(data);
  return categories.length === 1 && categories[0] === '分析服務 (A)';
};

export const isGeneralNoSampleOrder = (data = {}) => {
  return data?.sampleType === '無送樣' || isAnalysisOnlyOrder(data);
};

const normalizeServiceCatalog = (serviceCatalog = {}) => {
  if (!serviceCatalog || typeof serviceCatalog !== 'object') {
    return [];
  }

  return Object.values(serviceCatalog).flatMap(category => (
    Array.isArray(category) ? category : []
  ));
};

export const findServiceConfigByValue = (serviceCatalog = {}, serviceName = '') => {
  if (!serviceName) return null;

  return normalizeServiceCatalog(serviceCatalog).find(item => item?.value === serviceName) || null;
};

export const getServiceQuantityUnit = ({
  serviceCatalog = {},
  serviceName = ''
} = {}) => {
  const serviceConfig = findServiceConfigByValue(serviceCatalog, serviceName);
  return serviceConfig?.quantityUnit || '';
};

const getSelectedAnalysisServiceRows = (formData = {}) => (
  Array.isArray(formData.serviceItems)
    ? formData.serviceItems
      .filter(item => item?.category === '分析服務 (A)')
      .flatMap(item => Array.isArray(item.services) ? item.services : [])
      .filter(service => service?.service)
    : []
);

// R-M 開頭為試劑借出品項，不對應送樣樣本，因此不需要填 Sample_Name。
const REAGENT_LOAN_SERVICE_PREFIX = 'R-M';

export const isReagentLoanService = (serviceName = '') => (
  String(serviceName || '').trim().toUpperCase().startsWith(REAGENT_LOAN_SERVICE_PREFIX)
);

export const analysisServiceRequiresSampleSheetLite = ({
  serviceCatalog = {},
  serviceName = ''
} = {}) => {
  const serviceConfig = findServiceConfigByValue(serviceCatalog, serviceName);

  if (typeof serviceConfig?.requiresSampleSheetLite === 'boolean') {
    return serviceConfig.requiresSampleSheetLite;
  }

  return !isReagentLoanService(serviceConfig?.value || serviceName);
};

export const requiresAnalysisSampleSheetLite = ({
  formData = {},
  serviceCatalog = {}
} = {}) => {
  if (!isAnalysisOnlyOrder(formData)) return false;

  const analysisServices = getSelectedAnalysisServiceRows(formData);
  if (analysisServices.length === 0) return true;

  return analysisServices.some(service => analysisServiceRequiresSampleSheetLite({
    serviceCatalog,
    serviceName: service.service
  }));
};

export const getAnalysisSampleSheetLiteQuantity = ({
  formData = {},
  serviceCatalog = {}
} = {}) => {
  if (!requiresAnalysisSampleSheetLite({ formData, serviceCatalog })) {
    return 0;
  }

  const quantities = getSelectedAnalysisServiceRows(formData)
    .filter(service => analysisServiceRequiresSampleSheetLite({
      serviceCatalog,
      serviceName: service.service
    }))
    .map(service => parseInt(service?.quantity, 10))
    .filter(quantity => Number.isFinite(quantity) && quantity > 0);

  return quantities.length > 0 ? Math.max(...quantities) : 0;
};

/**
 * ChIP-seq 和 scRNA-seq 的 SC04 一樣，一個樣本會有多份 fastq，列數和步驟三填的樣本數
 * 對不起來，所以列數完全交給使用者增減（至少一列），樣品數量則維持步驟三填的值。
 */
export const isChIPseqAnalysisOrder = ({
  formData = {},
  serviceCatalog = {}
} = {}) => getSelectedAnalysisServiceRows(formData).some(service => isChIPseqServiceConfig(
  findServiceConfigByValue(serviceCatalog, service.service),
  service.service
));

const parsePositiveInteger = value => {
  const text = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

/**
 * 這些品項的 Sample Sheet 列數本來就不等於樣品數量：
 * - scRNA-seq SC02 / SC06 是 multiplex（列數較多），SC04 一個樣本多份 fastq（列數較少）
 * - ChIP-seq 的版面固定成對（ChIP 一列、input 一列），沒送的 input 那列會留白，
 *   而留白的列不算送樣也不計價，所以列數會多於樣品數量
 */
export const allowsSampleSheetRowCountMismatch = ({
  formData = {},
  serviceCatalog = {}
} = {}) => (
  getScRnaExperimentSampleSheetRule(formData?.scRNAExperimentInfo?.serviceName || '').unlockRowCount === true
  || isChIPseqAnalysisOrder({ formData, serviceCatalog })
);

/** ChIP-seq 的樣品數量：實際填了 Sample_Name 的列數。 */
export const getChIPseqSubmittedSampleCount = (formData = {}) => (
  countChIPseqSubmittedSamples(formData?.sampleInfo?.sampleSheet)
);

export const getAnalysisSampleCountConsistencyError = ({
  formData = {},
  serviceCatalog = {}
} = {}) => {
  if (!requiresAnalysisSampleSheetLite({ formData, serviceCatalog })) return '';

  const sampleCount = parsePositiveInteger(formData.sampleCount);
  if (sampleCount === null) {
    return '分析服務的樣本數必須是正整數';
  }

  const sampleRows = Array.isArray(formData.sampleInfo?.sampleSheet)
    ? formData.sampleInfo.sampleSheet
    : [];

  if (allowsSampleSheetRowCountMismatch({ formData, serviceCatalog })) {
    return sampleRows.length > 0 ? '' : 'Sample Sheet 至少要有一列';
  }

  if (sampleRows.length !== sampleCount) {
    return `樣本數（${sampleCount}）與 Sample Sheet 列數（${sampleRows.length}）不一致`;
  }

  return '';
};

export const normalizeNoSampleOrderData = (data = {}, {
  hasAnalysisSampleSheetLite = isAnalysisOnlyOrder(data)
} = {}) => {
  if (!isGeneralNoSampleOrder(data)) return data;

  const analysisOnlySampleCount = isAnalysisOnlyOrder(data)
    ? (hasAnalysisSampleSheetLite ? (data.sampleCount ?? '') : '')
    : '';

  return {
    ...data,
    sampleType: '無送樣',
    sampleCount: analysisOnlySampleCount,
    preservationMethod: '其他',
    shippingMethod: '其他'
  };
};

export const createSampleInfoContext = ({
  formData = {},
  apPackageInfo = null,
  getAllowedSampleTypes = () => [],
  getSampleTypeRestrictionMessage = () => '',
  getSpeciesMode = () => 'legacy',
  serviceCatalog = {}
} = {}) => {
  const normalizeSampleTypeList = (value) => (
    (Array.isArray(value) ? value : String(value || '').split(/[,，]/))
      .map(type => String(type).trim())
      .filter(Boolean)
  );

  const isAPPackage = Array.isArray(formData.selectedServiceCategories)
    && formData.selectedServiceCategories.includes('套組產品 (P)');

  const speciesMode = getSpeciesMode(formData.serviceItems || []);
  const analysisFlowInfo = getRNAseqPipelineInfo({
    serviceItems: formData.serviceItems || [],
    serviceCatalog
  }) || getScRNAseqPipelineInfo({
    serviceItems: formData.serviceItems || [],
    serviceCatalog
  }) || getChIPseqPipelineInfo({
    serviceItems: formData.serviceItems || [],
    serviceCatalog
  });

  const apBindingSampleTypes = isAPPackage && apPackageInfo?.binding?.sampleType
    ? normalizeSampleTypeList(apPackageInfo.binding.sampleType)
    : [];

  const allowedSampleTypes = (() => {
    if (apBindingSampleTypes.length > 0) {
      return apBindingSampleTypes;
    }
    return normalizeSampleTypeList(getAllowedSampleTypes());
  })();

  const restrictionMessage = apBindingSampleTypes.length > 0
    ? `📦 此為套組產品，樣品類型限制為：${allowedSampleTypes.join('、')}`
    : getSampleTypeRestrictionMessage();

  return {
    isAPPackage,
    apPackageInfo,
    speciesMode,
    analysisFlowInfo,
    allowedSampleTypes,
    restrictionMessage
  };
};

export const buildSampleInfoPreviewItems = ({
  formData = {},
  resolvedSampleFormData = formData,
  isGeneralNoSampleOrder: generalNoSampleOrder = false,
  speciesMode = 'legacy',
  analysisFlowInfo = null,
  analysisParameterSummary = [],
  getSpeciesDisplayInfo = () => ({
    primary: '未填寫',
    details: [],
    tenx: []
  })
} = {}) => {
  const items = [
    {
      key: 'sampleType',
      type: 'text',
      label: '樣品類型',
      value: resolvedSampleFormData.sampleType
    }
  ];

  if (resolvedSampleFormData.sampleType === '其他') {
    items.push({
      key: 'sampleTypeOther',
      type: 'text',
      label: '樣品類型說明',
      value: resolvedSampleFormData.sampleTypeOther
    });
  }

  items.push(
    {
      key: 'sampleCount',
      type: 'text',
      label: '樣品數量',
      value: resolvedSampleFormData.sampleCount
    },
    {
      key: 'preservationMethod',
      type: 'text',
      label: '保存方式',
      value: resolvedSampleFormData.preservationMethod
    }
  );

  if (resolvedSampleFormData.preservationMethod === '其他') {
    items.push({
      key: 'preservationMethodOther',
      type: 'text',
      label: '保存方式說明',
      value: resolvedSampleFormData.preservationMethodOther
    });
  }

  if (speciesMode === 'scrna' && formData.scRNAExperimentInfo) {
    items.push(
      {
        key: 'scRNAServiceName',
        type: 'text',
        label: 'ServiceName',
        value: formData.scRNAExperimentInfo.serviceName || ''
      },
      {
        key: 'scRNADescription',
        type: 'text',
        label: 'description',
        value: formData.scRNAExperimentInfo.description || '',
        colSpan: 2
      }
    );
  }

  items.push({
    key: 'species',
    type: 'species',
    label: '物種',
    speciesMode,
    speciesInfo: getSpeciesDisplayInfo(formData)
  });

  if (analysisFlowInfo) {
    items.push({
      key: 'analysisFlow',
      type: 'analysisFlow',
      label: '分析流程',
      analysisFlowInfo
    });
  }

  if (analysisParameterSummary.length > 0) {
    items.push({
      key: 'analysisParameters',
      type: 'analysisParameters',
      label: '分析參數',
      analysisParameterSummary
    });
  }

  items.push({
    key: 'shippingMethod',
    type: 'text',
    label: '寄送方式',
    value: resolvedSampleFormData.shippingMethod
  });

  if (resolvedSampleFormData.shippingMethod === '其他') {
    items.push({
      key: 'shippingMethodOther',
      type: 'text',
      label: '寄送方式說明',
      value: resolvedSampleFormData.shippingMethodOther
    });
  }

  if (resolvedSampleFormData.sampleType === 'Library') {
    items.push({
      key: 'concMethod',
      type: 'text',
      label: '濃度測定',
      value: formData.libraryInfo?.concMethod
    });
  } else if (!generalNoSampleOrder) {
    items.push({
      key: 'concMethod',
      type: 'text',
      label: '濃度測定',
      value: formData.sampleInfo?.concMethod
    });
  }

  return items;
};

export const buildSampleInfoPreviewModel = (args = {}) => {
  const {
    formData = {},
    resolvedSampleFormData = formData,
    isGeneralNoSampleOrder: generalNoSampleOrder = false,
    isAnalysisOnlyOrder: analysisOnlyOrder = false,
    showAnalysisSampleSheetLite = analysisOnlyOrder,
    // ChIP-seq 的列序有意義（ChIP 一列、input 一列），留白的 input 不能被擠掉，
    // 否則預覽的序號與 ChIP / Input 標示會對不起來。
    keepBlankSampleRows = false
  } = args;

  const items = buildSampleInfoPreviewItems(args);
  const allSampleRows = Array.isArray(formData.sampleInfo?.sampleSheet)
    ? formData.sampleInfo.sampleSheet
    : [];
  const sampleRows = keepBlankSampleRows
    ? allSampleRows
    : allSampleRows.filter(row => row.sampleName);

  return {
    items,
    speciesInfo: items.find(item => item.type === 'species')?.speciesInfo || null,
    noSampleNotice: generalNoSampleOrder && !analysisOnlyOrder
      ? '⚠️ 目前為無送樣案件，不需填寫樣本資訊。'
      : '',
    librarySampleSheetRows: resolvedSampleFormData.sampleType === 'Library'
      ? (Array.isArray(formData.libraryInfo?.sampleSheet) ? formData.libraryInfo.sampleSheet.filter(row => row.sampleName) : [])
      : [],
    libraryDetailRows: resolvedSampleFormData.sampleType === 'Library'
      ? (Array.isArray(formData.libraryInfo?.librarySampleSheet) ? formData.libraryInfo.librarySampleSheet.filter(row => row.sampleName) : [])
      : [],
    sampleRows: resolvedSampleFormData.sampleType !== 'Library'
      ? (analysisOnlyOrder
        ? (showAnalysisSampleSheetLite ? sampleRows : [])
        : (!generalNoSampleOrder ? sampleRows : []))
      : [],
    notes: formData.notes || ''
  };
};
